import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { StockComparisonSnapshot } from "../../../types/StockComparison";
import { formatStockPrice } from "../../../utils/stockPriceFormatter";
import { useCompanyProfile } from "../Charts/hooks/useCompanyProfile";

interface ComparisonTechnicalPanelProps {
  snapshot: StockComparisonSnapshot | null;
  color: string;
}

// ─── 52-Week Range Bar ────────────────────────────────────────────────────────

interface RangeBarProps {
  price: number | null;
  yearLow: number | null;
  yearHigh: number | null;
  color: string;
  currency?: string;
}

const RangeBar = ({
  price,
  yearLow,
  yearHigh,
  color,
  currency,
}: RangeBarProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const trackBg = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const textMuted = theme.palette.text.secondary;
  const textPrimary = theme.palette.text.primary;
  const surfaceBg = isDark ? "#212121" : theme.palette.background.paper;

  const hasData =
    price !== null &&
    yearLow !== null &&
    yearHigh !== null &&
    yearHigh > yearLow;

  const positionPct = hasData
    ? Math.max(
        0,
        Math.min(100, ((price! - yearLow!) / (yearHigh! - yearLow!)) * 100),
      )
    : 0;

  const fromHighPct = hasData ? ((price! - yearHigh!) / yearHigh!) * 100 : null;
  const fromHighLabel =
    fromHighPct !== null
      ? fromHighPct >= 0
        ? `+${fromHighPct.toFixed(1)}% from 52W high`
        : `${fromHighPct.toFixed(1)}% from 52W high`
      : null;

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.6875rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: textMuted,
          fontWeight: 600,
          mb: 1.5,
        }}
      >
        52-Week Range
      </Typography>

      {/* XS: compact text-only view */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: textPrimary }}>
            {yearLow !== null ? formatStockPrice({ price: yearLow, currency }) : "—"}
          </Typography>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: textPrimary }}>
            {yearHigh !== null ? formatStockPrice({ price: yearHigh, currency }) : "—"}
          </Typography>
        </Box>
        {fromHighLabel && (
          <Typography sx={{ fontSize: "0.6875rem", color: textMuted, fontWeight: 500, mt: 0.5 }}>
            {fromHighLabel}
          </Typography>
        )}
      </Box>

      {/* sm+: full range bar */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: textPrimary, flexShrink: 0 }}>
            {yearLow !== null ? formatStockPrice({ price: yearLow, currency }) : "—"}
          </Typography>

          <Box sx={{ position: "relative", flex: 1, py: 0.75 }}>
            <Box sx={{ height: 5, borderRadius: "3px", backgroundColor: trackBg }} />
            {hasData && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  height: 5,
                  width: `${positionPct}%`,
                  backgroundColor: color,
                  borderRadius: "3px",
                }}
              />
            )}
            {hasData && (
              <Box
                sx={{
                  position: "absolute",
                  left: `${positionPct}%`,
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: color,
                  border: `2.5px solid ${surfaceBg}`,
                  zIndex: 1,
                }}
              />
            )}
          </Box>

          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: textPrimary, flexShrink: 0 }}>
            {yearHigh !== null ? formatStockPrice({ price: yearHigh, currency }) : "—"}
          </Typography>
        </Box>

        {fromHighLabel && (
          <Typography sx={{ fontSize: "0.6875rem", color: textMuted, fontWeight: 500, textAlign: "right" }}>
            {fromHighLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ─── MA Deviation Chart ───────────────────────────────────────────────────────

// Returns a symmetric axis range and tick interval scaled to the actual value,
// always targeting 5 ticks (-max, -half, 0, +half, +max) with ≥35% headroom.
const computeAxisRange = (
  absValue: number,
): { max: number; interval: number } => {
  const minInterval = (absValue * 1.35) / 2;
  const candidates = [
    5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200, 300, 500,
  ];
  const interval = candidates.find((c) => c >= minInterval) ?? 500;
  const max = Math.max(interval * 2, 20);
  return { max, interval: max / 2 };
};

interface MaDeviationChartProps {
  vsAvg200Pct: number | null;
}

const MaDeviationChart = ({ vsAvg200Pct }: MaDeviationChartProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const textMuted = theme.palette.text.secondary;
  const positiveColor = "#22c55e";
  const negativeColor = theme.palette.error.main;
  const neutralColor = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)";
  const zeroDash = isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)";

  const CHART_H = 120;
  const HALF_H = CHART_H / 2;
  const BAR_W = 56;

  const hasData = vsAvg200Pct !== null;
  const value = vsAvg200Pct ?? 0;
  const { max: MAX_RANGE, interval } = computeAxisRange(Math.abs(value));
  const isPositive = value >= 0;
  const barHeightPx =
    (Math.min(Math.abs(value), MAX_RANGE) / MAX_RANGE) * HALF_H;
  const barColor = hasData
    ? isPositive
      ? positiveColor
      : negativeColor
    : neutralColor;

  const sign = isPositive ? "+" : "";

  const ticks = [MAX_RANGE, interval, 0, -interval, -MAX_RANGE];

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.6875rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: textMuted,
          fontWeight: 600,
          mb: 3,
        }}
      >
        200-Day MA Deviation
      </Typography>

      <Box sx={{ display: "flex", gap: 0.5 }}>
        {/* Y-axis labels */}
        <Box
          sx={{
            position: "relative",
            width: { xs: 36, sm: 52 },
            overflow: "visible",
            flexShrink: 0,
          }}
          style={{ height: CHART_H }}
        >
          {ticks.map((tick) => {
            const topPct = ((MAX_RANGE - tick) / (2 * MAX_RANGE)) * 100;
            return (
              <Typography
                key={tick}
                sx={{
                  position: "absolute",
                  right: 4,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "text.primary",
                  lineHeight: 1,
                }}
                style={{ top: `${topPct}%`, transform: "translateY(-50%)" }}
              >
                {tick > 0 ? `+${tick}` : tick}%
              </Typography>
            );
          })}
        </Box>

        {/* Chart area */}
        <Box sx={{ flex: 1, position: "relative" }} style={{ height: CHART_H }}>
          {/* Grid lines */}
          {ticks.map((tick) => {
            const topPct = ((MAX_RANGE - tick) / (2 * MAX_RANGE)) * 100;
            const isZero = tick === 0;
            return (
              <Box
                key={tick}
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  height: "1px",
                  ...(isZero
                    ? {
                        backgroundImage: `repeating-linear-gradient(to right, ${zeroDash} 0, ${zeroDash} 4px, transparent 4px, transparent 7px)`,
                      }
                    : {
                        backgroundColor: theme.palette.divider,
                        opacity: 0.4,
                      }),
                }}
                style={{ top: `${topPct}%` }}
              />
            );
          })}

          {/* Bar */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              width: BAR_W,
              backgroundColor: barColor,
              borderRadius: "2px",
            }}
            style={{
              top: isPositive ? HALF_H - barHeightPx : HALF_H,
              height: Math.max(barHeightPx, hasData ? 2 : 0),
            }}
          />

          {/* Value label — matches BarValueLabelsLayer style */}
          {hasData && (
            <Typography
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "text.primary",
                fontFamily: "system-ui, -apple-system, sans-serif",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
              style={{
                top: isPositive
                  ? HALF_H - barHeightPx - 18
                  : HALF_H + barHeightPx + 4,
              }}
            >
              {sign}
              {value.toFixed(1)}%
            </Typography>
          )}
        </Box>

        {/* Right spacer — mirrors the Y-axis width so the bar sits centred */}
        <Box sx={{ width: { xs: 8, sm: 22 }, flexShrink: 0 }} />
      </Box>
    </Box>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

const ComparisonTechnicalPanel = ({
  snapshot,
  color,
}: ComparisonTechnicalPanelProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const { data: profileData } = useCompanyProfile(snapshot?.ticker ?? "");
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;
  const currency = profile?.currency;

  const border = theme.palette.divider;
  const textMuted = theme.palette.text.secondary;
  const textPrimary = theme.palette.text.primary;
  const headerBg = theme.palette.background.default;
  const surfaceBg = isDark ? "#212121" : theme.palette.background.paper;

  if (snapshot === null) {
    return (
      <Box
        sx={{
          flex: 1,
          border: `1px solid ${border}`,
          borderRadius: 1,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
          p: 4,
          backgroundColor: headerBg,
        }}
      >
        <Box
          sx={{
            border: `1px dashed ${border}`,
            borderRadius: 1,
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.875rem", color: textMuted }}>
            Add a stock to compare
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        border: `1px solid ${border}`,
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: headerBg,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: textPrimary,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          Price & Technical
        </Typography>
      </Box>

      {/* Body */}
      <Box
        sx={{
          px: { xs: 1, sm: 2.5 },
          py: 2.5,
          backgroundColor: surfaceBg,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <RangeBar
          price={snapshot.price}
          yearLow={snapshot.yearLow}
          yearHigh={snapshot.yearHigh}
          color={color}
          currency={currency}
        />

        <Box sx={{ height: "1px", backgroundColor: border }} />

        <MaDeviationChart vsAvg200Pct={snapshot.vsAvg200Pct} />
      </Box>
    </Box>
  );
};

export default ComparisonTechnicalPanel;
